// "A summer sunset": a blazing evening at the ghat → Chhath at the water's edge, tap to offer arghya → gratitude as diyas float out and the sun sinks → her painting emerges.
export const summerSunset = {
  slug: "a-summer-sunset",
  duration: 23.5,
  // pure ambience: the evening water, birds and the arghya (no music bed)
  quiet: true,
  captionTail: "The sun nurtures every living soul.",
  stages: [
    { id: "blaze", at: 0, hint: "a blazing summer evening" },
    { id: "trees", at: 5, hint: "tall trees before the sun" },
    { id: "chhath", at: 11, hint: "tap to offer arghya" },
    { id: "gratitude", at: 15.5, hint: "gratitude" },
    { id: "settle", at: 19.5, hint: "" },
  ],
  touch: { kind: "arghya", from: 11.5, until: 15.5 },
  // the blazing ghat at Chhath: soop baskets of fruit, sugarcane and diyas at the water's edge; diyas drift out as the sun sinks
  layers: [
    // her composition first: tall dark trees in front of the blazing sun, the reflection below in purples and pinks
    { src: "/arcs/sunset/trees.webp", wrap: 3.48, th: 0, h: 8.4, y: 1.8, r: 4.9, water: 0.38, show: [-1, 0, 99, 99] },
    // the devotees come only near the end, for the evening arghya, then fade, leaving the trees and the sun to become the painting
    { src: "/arcs/sunset/chhath.webp", wrap: 3.48, th: 0, h: 8.4, y: 1.8, r: 4.9, water: 0.38, show: [10.5, 12.5, 16, 18.5] },
    // the arghya: hands lift a brass lota and a sparkling stream pours toward the sun
    { src: "/arcs/sunset/arghya.webp", black: true, glow: 0.25, sway: 0.02, th: 0.05, y: 1.05, w: 1.9, h: 1.9, r: 3.4, show: [12, 13, 15.5, 16.8] },
    { src: "/arcs/ganges/diyas.webp", add: true, flicker: 0.35, th: 0.3, y: 0.3, w: 3.2, h: 2.1, r: 4.3, drift: [15, 23, -0.1, 0.5], show: [15, 17, 99, 99] },
  ],
  line: { text: "Even the setting sun is worshipped, for it promises to rise again.", from: 17, to: 31, pos: "low" },
  cues: [[11.2, "conch"], [13.2, "pour"], [14.6, "bellSoft"]],
  ambient: { scene: "ghat", until: 99, then: [] },
  look: { start: 0.04, lift: [0, 1], end: 0.04, yaw: [[0, 0.4], [5, -0.3], [7, 0], [23.5, 0]] },
  calm: { duration: 12, fade: [4, 8.5], line: [5.5, 11.5], backdrop: "chhath", pos: "low" },
  speed: [[0, 1], [23.5, 1]],
  reveal: { style: "horizontal", from: [0.5, 0.45], duration: 10, focus: [{ rect: [0, 0.3, 1, 0.25], style: "flow" }] },
  frag: `
uniform sampler2D uMap;
uniform vec4 uCrop;
uniform vec4 uMark[8];
uniform float uT, uAspect, uTileH, uRadius, uCy, uSurface;
varying vec3 vPos;
${"__NOISE__"}
float hash1(float n){ return fract(sin(n * 91.7) * 43758.5453); }
float hash2(vec2 p){ return fract(sin(dot(p, vec2(41.3, 289.1))) * 43758.5453); }
float seg(vec2 p, vec2 a, vec2 b){ vec2 pa = p - a, ba = b - a; float h = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0); return length(pa - ba * h); }
float flame(vec2 p, float h){
  float s = clamp((p.y + 0.2 * h) / (1.2 * h), 0.0, 1.0);
  float w = 0.55 * h * sqrt(s) * (1.0 - s);
  return (1.0 - smoothstep(w * 0.6, w, abs(p.x + 0.1 * h * sin(uT * 9.0 + p.y * 40.0)))) * step(-0.2 * h, p.y) * step(p.y, h);
}
// the blazing sky: horizontal strokes of red, orange and yellow, the sun sinking
vec3 sky(vec2 P, float sunY){
  float st = snoise(vec3(P.x * 0.2, P.y * 3.0, 1.0)) * 0.5 + 0.5;
  vec3 c = mix(vec3(1.0, 0.85, 0.3), vec3(0.98, 0.5, 0.12), smoothstep(1.6, 3.0, P.y));
  c = mix(c, vec3(0.85, 0.15, 0.12), smoothstep(2.8, 5.5, P.y));
  c = mix(c, c * vec3(1.05, 0.8, 0.75), smoothstep(0.55, 0.85, st));
  float sd = length(P - vec2(0.0, sunY));
  c += vec3(1.0, 0.8, 0.4) * exp(-sd * 1.3) * 0.5;
  return mix(c, vec3(1.0, 0.93, 0.65), 1.0 - smoothstep(0.5, 0.53, sd));
}
// a devotee in the water, seen from behind: a flat gouache figure in a bright sari, palms raised to the sun
vec4 devotee(vec2 p, vec3 sari){
  float body = step(abs(p.x), 0.07 + p.y * 0.06) * step(-0.02, p.y) * step(p.y, 0.2);
  float head = 1.0 - smoothstep(0.035, 0.042, length(p - vec2(0.0, 0.26)));
  float pallu = 1.0 - smoothstep(0.045, 0.052, length((p - vec2(0.0, 0.25)) * vec2(1.0, 0.9)));
  float arms = step(abs(abs(p.x) - 0.09), 0.018) * step(0.16, p.y) * step(p.y, 0.33);
  vec3 c = mix(sari, vec3(0.08, 0.05, 0.05), head * (1.0 - step(0.25, p.y - 0.0) * 0.0) * 0.0);
  c = mix(sari * 0.9, vec3(0.45, 0.28, 0.2), arms * step(0.27, p.y));
  float a = max(max(body, pallu), arms);
  return vec4(c, a);
}
void main(){
  float n = snoise(vPos * 0.6) * 0.5 + 0.5;
  float th = atan(vPos.x, -vPos.z);
  float grat = smoothstep(14.5, 18.5, uT);
  float sunY = mix(2.0, 1.15, smoothstep(13.0, 21.0, uT));
  vec3 col;
  vec2 local;
  if (uSurface < 0.5) {
    float X = th * uRadius;
    vec2 P = vec2(X, vPos.y);
    // heat shimmer near the horizon
    vec2 Ps = P + vec2(0.0, 0.03 * snoise(vec3(P.x * 3.0, P.y * 6.0, uT * 1.5)) * smoothstep(3.0, 1.4, P.y));
    col = sky(Ps, sunY);
    // the dark treeline
    float tl = 1.3 + 0.35 * (0.5 + 0.5 * snoise(vec3(P.x * 0.8, 0.0, 5.0))) + 0.2 * abs(sin(P.x * 3.1));
    if (P.y < tl && P.y > 1.25) col = vec3(0.1, 0.05, 0.08) * (0.8 + 0.3 * n);
    // the water: the sky's fire mirrored below, with purples and pinks
    if (P.y <= 1.25) {
      vec2 R = vec2(P.x + 0.05 * sin(P.y * 25.0 + uT * 1.2), 2.5 - P.y);
      vec3 refl = R.y < tl ? vec3(0.12, 0.06, 0.12) : sky(R, sunY);
      refl = mix(refl, refl * vec3(0.8, 0.55, 0.95) + vec3(0.12, 0.0, 0.12), 0.35 + 0.35 * grat);
      float strokes = snoise(vec3(P.x * 0.4, P.y * 16.0, uT * 0.1)) * 0.5 + 0.5;
      col = refl * (0.75 + 0.3 * strokes);
      float gl = exp(-P.x * P.x * 2.0) * smoothstep(0.6, 0.9, snoise(vec3(P.x * 3.0, P.y * 30.0, uT * 0.8)) * 0.5 + 0.5);
      col += vec3(1.0, 0.9, 0.6) * gl * 0.5;
      // devotees standing waist-deep, facing the sun
      for (int i = 0; i < 5; i++){
        float fi = float(i);
        vec2 dp = vec2(-2.2 + fi * 1.1 + 0.3 * hash1(fi), 0.98 + 0.06 * hash1(fi + 3.0));
        vec3 sari = fi < 1.0 ? vec3(0.95, 0.75, 0.1) : fi < 2.0 ? vec3(0.9, 0.2, 0.12) : fi < 3.0 ? vec3(0.98, 0.5, 0.1) : fi < 4.0 ? vec3(0.95, 0.8, 0.2) : vec3(0.85, 0.15, 0.2);
        vec4 d = devotee((P - dp) / 0.85, sari);
        col = mix(col, d.rgb, d.a * (1.0 - grat * 0.4));
      }
      // diyas floating out on leaf boats in gratitude
      for (int i = 0; i < 7; i++){
        float fi = float(i);
        float k = smoothstep(14.5 + fi * 0.4, 19.5 + fi * 0.4, uT);
        vec2 dc = vec2(-1.5 + fi * 0.5 + 0.4 * sin(fi * 2.0) * k, mix(0.5, 1.05, k));
        float sc = mix(1.0, 0.45, k);
        vec2 q = (P - dc) / sc;
        float boat = step(abs(q.x), 0.08 - q.y * q.y * 0.0) * step(abs(q.y + 0.01), 0.012);
        col = mix(col, vec3(0.25, 0.4, 0.15), boat * step(0.01, k));
        col = mix(col, vec3(1.0, 0.85, 0.4), flame(q - vec2(0.0, 0.01), 0.06) * step(0.01, k));
        col += vec3(1.0, 0.6, 0.2) * exp(-dot(q, q) * 400.0) * step(0.01, k) * 0.3;
      }
    }
    // the ghat steps, decorated with alpana, at the water's edge
    if (P.y < 0.5) {
      col = mix(vec3(0.55, 0.38, 0.3), vec3(0.42, 0.28, 0.24), step(0.5, fract(P.y * 6.0))) * (0.85 + 0.2 * n);
      float alp = 1.0 - smoothstep(0.006, 0.014, abs(fract(P.y * 6.0) - 0.15 - 0.04 * sin(P.x * 14.0)));
      col = mix(col, mix(vec3(0.95, 0.92, 0.85), vec3(0.85, 0.15, 0.1), step(0.5, fract(P.x * 2.0))), alp * 0.8);
    }
    // the kosi: sugarcane stalks tied into a canopy, earthen diyas beneath
    vec2 kc = vec2(-2.0, 0.35);
    for (int i = 0; i < 5; i++){
      float fi = float(i);
      vec2 base = kc + vec2(-0.35 + fi * 0.175, 0.0);
      float d = seg(P, base, kc + vec2(0.0, 1.25));
      float cane = 1.0 - smoothstep(0.014, 0.022, d);
      float node = step(0.85, fract((P.y - kc.y) * 7.0));
      col = mix(col, mix(vec3(0.28, 0.55, 0.2), vec3(0.2, 0.35, 0.12), node), cane * step(P.y, kc.y + 1.25));
      col = mix(col, vec3(0.35, 0.65, 0.25), (1.0 - smoothstep(0.0, 0.02, abs(P.y - (kc.y + 1.25 + (P.x - kc.x) * (fi - 2.0) * 0.6)))) * step(abs(P.x - kc.x - (fi - 2.0) * 0.12), 0.14) * step(kc.y + 1.2, P.y));
    }
    col = mix(col, vec3(0.85, 0.12, 0.1), step(abs(P.x - kc.x), 0.05) * step(abs(P.y - kc.y - 1.05), 0.04));
    for (int i = 0; i < 4; i++){
      vec2 dq = P - (kc + vec2(-0.24 + float(i) * 0.16, 0.05));
      col = mix(col, vec3(0.55, 0.28, 0.15), (1.0 - smoothstep(0.035, 0.045, length(dq * vec2(1.0, 2.4)))) * step(dq.y, 0.0));
      col = mix(col, vec3(1.0, 0.85, 0.4), flame(dq - vec2(0.0, 0.01), 0.08));
    }
    // bamboo soops and a daura laden with banana, coconut and citrus, thekua and sindoor
    for (int i = 0; i < 2; i++){
      float fi = float(i);
      vec2 sq = P - vec2(1.5 + fi * 0.75, 0.36);
      float soop = step(abs(sq.x), 0.3 - sq.y * 0.6) * step(-0.12, sq.y) * step(sq.y, 0.12);
      float weave = 0.85 + 0.15 * step(0.5, fract(sq.x * 30.0 + sq.y * 30.0));
      col = mix(col, vec3(0.78, 0.62, 0.32) * weave, soop);
      float banana = (1.0 - smoothstep(0.01, 0.018, abs(length(sq - vec2(-0.08, 0.12)) - 0.12))) * step(sq.y, 0.06) * step(-0.02, sq.y) * step(abs(sq.x + 0.08), 0.1);
      col = mix(col, vec3(0.98, 0.85, 0.2), banana * soop);
      col = mix(col, vec3(0.45, 0.28, 0.15), (1.0 - smoothstep(0.04, 0.05, length(sq - vec2(0.1, 0.03)))));
      col = mix(col, vec3(1.0, 0.55, 0.1), (1.0 - smoothstep(0.028, 0.035, length(sq - vec2(0.0, -0.02)))));
      col = mix(col, vec3(0.62, 0.38, 0.16), (1.0 - smoothstep(0.022, 0.028, length(sq - vec2(-0.14, -0.05)))) * (0.8 + 0.2 * step(0.5, fract(atan(sq.y + 0.05, sq.x + 0.14) * 2.0))));
      col = mix(col, vec3(0.85, 0.05, 0.08), (1.0 - smoothstep(0.012, 0.02, length(sq - vec2(0.17, -0.07)))));
    }
    // arghya: softly painted hands lift a brass lota, water pouring toward the sun
    float last = uT > 10.5 ? 10.5 : -9.0;
    for (int i = 0; i < 8; i++) last = max(last, uMark[i].w > 0.0 ? uMark[i].w : -9.0);
    float pour = smoothstep(last, last + 0.5, uT) * (1.0 - smoothstep(last + 3.0, last + 4.0, uT)) * (1.0 - grat);
    vec2 lq = P - vec2(0.0, 1.55 + 0.08 * pour);
    float lota = (1.0 - smoothstep(0.1, 0.11, length(lq * vec2(1.0, 1.15)))) + step(abs(lq.x), 0.045) * step(0.06, lq.y) * step(lq.y, 0.15);
    vec3 brass = mix(vec3(0.95, 0.72, 0.25), vec3(0.6, 0.4, 0.1), smoothstep(-0.1, 0.1, lq.x + lq.y));
    float hands = (1.0 - smoothstep(0.05, 0.06, length((lq - vec2(-0.12, -0.04)) * vec2(1.0, 0.6)))) + (1.0 - smoothstep(0.05, 0.06, length((lq - vec2(0.12, -0.04)) * vec2(1.0, 0.6))));
    hands += step(abs(abs(lq.x) - 0.13), 0.035) * step(lq.y, -0.06) * step(-0.5, lq.y);
    float show = smoothstep(last - 0.6, last, uT) * (1.0 - smoothstep(last + 3.5, last + 4.5, uT)) * step(-8.0, last) * (1.0 - grat);
    col = mix(col, vec3(0.55, 0.35, 0.25) * (0.9 + 0.15 * n), clamp(hands, 0.0, 1.0) * show);
    col = mix(col, brass, clamp(lota, 0.0, 1.0) * show);
    float sy = P.y - 1.66;
    float sx = 0.14 + 0.25 * max(0.0, -sy) * 0.3 + 0.015 * sin(sy * 30.0 + uT * 12.0);
    float stream = (1.0 - smoothstep(0.008, 0.02, abs(P.x - sx))) * step(sy, 0.0) * step(-0.9, sy);
    col = mix(col, vec3(0.92, 0.96, 1.0), stream * pour * 0.85);
    col += vec3(1.0, 0.95, 0.75) * stream * pour * step(0.8, hash2(floor(vec2(P.y * 30.0, uT * 10.0)))) * 0.8;
    col += vec3(1.0, 0.6, 0.25) * 0.12 * grat;
    local = vec2(X, P.y - uCy);
  } else if (uSurface < 1.5) {
    vec2 p = vPos.xz;
    col = mix(vec3(0.5, 0.34, 0.28), vec3(0.42, 0.27, 0.22), step(0.5, fract(length(p) * 1.5))) * (0.85 + 0.2 * n);
    float ring = 1.0 - smoothstep(0.01, 0.03, abs(fract(length(p) * 1.5) - 0.2 - 0.05 * sin(atan(p.y, p.x) * 12.0)));
    col = mix(col, vec3(0.92, 0.88, 0.8), ring * 0.7);
    col += vec3(1.0, 0.55, 0.2) * 0.12 * (1.0 + grat);
    local = vPos.xz;
  } else {
    col = mix(vec3(0.9, 0.3, 0.15), vec3(0.6, 0.2, 0.35), grat);
    local = vPos.xz;
  }
  gl_FragColor = vec4(clamp(col, 0.0, 1.0), 1.0);
  #include <colorspace_fragment>
}`,
};
